// Updated SprintBoard.jsx to work with dynamic boards
import React, { useState, useEffect } from 'react';
import TicketCard from './TicketCard';
import LoadingSpinner from './LoadingSpinner';
import { Document, Packer, Paragraph, TextRun, AlignmentType } from "docx";
import { saveAs } from "file-saver";
import './SprintBoard.css';

function SprintBoard({ 
  name, 
  board, 
  tickets, 
  onDrop, 
  onReorder, 
  loading = false, 
  hideHeader = false 
}) {
  const [dragOverId, setDragOverId] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [lastDroppedKey, setLastDroppedKey] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [loadingReleaseNotes, setLoadingReleaseNotes] = useState(false);
  const [sortField, setSortField] = useState('order');
  const [sortOrder, setSortOrder] = useState('asc');

  useEffect(() => {
    if (loading) return;
    
    const handleClickOutsideMenu = (e) => {
      if (!e.target.closest('.board-menu-container')) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutsideMenu);
    return () => {
      document.removeEventListener('click', handleClickOutsideMenu);
    };
  }, [loading]);

  if (loading) {
    return (
      <div style={{ padding: '1rem' }}>
        <LoadingSpinner message={`Loading ${name}...`} />
      </div>
    );
  }

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOverId(null);
    setIsDragActive(false);

    const ticketData = e.dataTransfer.getData('ticket');
    const reorderData = e.dataTransfer.getData('reorder-ticket');

    let droppedTicket;
    if (ticketData) {
      droppedTicket = JSON.parse(ticketData);
    } else if (reorderData) {
      droppedTicket = JSON.parse(reorderData);
    }

    if (droppedTicket) {
      setLastDroppedKey(droppedTicket.issue_key);
      onDrop(e, name);
    }
  };

  const handleDragStartCard = (e, ticket) => {
    e.dataTransfer.setData('reorder-ticket', JSON.stringify({ ...ticket, fromBoard: name }));
    setIsDragActive(true);
  };

  const handleDragEnterCard = (e, targetTicket) => {
    e.preventDefault();
    setDragOverId(targetTicket.issue_key);
  };

  const handleDragLeaveCard = (e) => {
    e.preventDefault();
    setDragOverId(null);
  };

  const handleDropCard = (e, targetTicket) => {
    e.preventDefault();
    setDragOverId(null);
    setIsDragActive(false);

    const data = e.dataTransfer.getData('reorder-ticket');
    if (!data) return;
    const draggedTicket = JSON.parse(data);

    if (draggedTicket.fromBoard === name) {
      onReorder(draggedTicket.issue_key, targetTicket.issue_key, name);
    } else {
      onDrop(e, name);
    }
  };

  const handleDragEnterBoard = (e) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeaveBoard = (e) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const toggleMenu = () => {
    setMenuOpen((prev) => !prev);
  };

  const handleSortTickets = (field, order) => {
    setSortField(field);
    setSortOrder(order);
    setMenuOpen(false);
  };

  const sortedTickets = (() => {
    const ticketsCopy = [...tickets];
    if (sortField === 'order') {
      return ticketsCopy;
    }

    return ticketsCopy.sort((a, b) => {
      let aValue = '';
      let bValue = '';

      if (sortField === 'issue_key') {
        aValue = parseInt(a.issue_key?.split('-')[1], 10);
        bValue = parseInt(b.issue_key?.split('-')[1], 10);

        if (isNaN(aValue) || isNaN(bValue)) return 0;
      } else if (sortField === 'status') {
        aValue = (a.status || '').toLowerCase();
        bValue = (b.status || '').toLowerCase();
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  })();

  const handleGenerateReleaseNotes = async () => {
    const doneTickets = tickets.filter((ticket) => ticket.status?.toLowerCase() === 'done');

    if (doneTickets.length === 0) {
      alert('No "Done" tickets in this board.');
      return;
    }

    setLoadingReleaseNotes(true);

    try {
      const doc = new Document({
        sections: [
          {
            properties: {},
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: `Release Notes for ${name}`,
                    bold: true,
                    size: 48,
                  }),
                ],
                alignment: AlignmentType.CENTER,
              }),
              ...doneTickets.map((ticket) => 
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${ticket.issue_key}: ${ticket.summary}`,
                      bold: true,
                    }),
                  ],
                })
              ),
            ],
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, `Release_Notes_${name.replace(/\s+/g, '_')}.docx`);
    } catch (error) {
      console.error('Error generating release notes:', error);
      alert('Error generating release notes');
    } finally {
      setLoadingReleaseNotes(false);
      setMenuOpen(false);
    }
  };

  return (
    <div
      className={`sprint-board-content ${isDragActive ? 'drag-over' : ''}`}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragEnter={handleDragEnterBoard}
      onDragLeave={handleDragLeaveBoard}
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {loadingReleaseNotes && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Generating Release Notes...</p>
        </div>
      )}

      {/* Only show default header if hideHeader is false */}
      {!hideHeader && (
        <div className="board-header">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {name}
            <span className="task-count-badge">{tickets.length}</span>
          </h3>
          <div className="board-menu-container" style={{ position: 'relative' }}>
            <div className="board-menu" onClick={toggleMenu}>⋮</div>
            {menuOpen && (
              <div className="menu-dropdown">
                <button onClick={handleGenerateReleaseNotes}>📝 Generate Release Notes</button>
                <button onClick={() => handleSortTickets('issue_key', 'asc')}>🔼 Sort Issue Key ↑</button>
                <button onClick={() => handleSortTickets('issue_key', 'desc')}>🔽 Sort Issue Key ↓</button>
                <button onClick={() => handleSortTickets('status', 'asc')}>🔼 Sort Status A → Z</button>
                <button onClick={() => handleSortTickets('status', 'desc')}>🔽 Sort Status Z → A</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Menu for custom header */}
      {hideHeader && (
        <div style={{ 
          position: 'absolute', 
          top: '8px', 
          right: '8px', 
          zIndex: 10 
        }}>
          <div className="board-menu-container" style={{ position: 'relative' }}>
            <button
              onClick={toggleMenu}
              style={{
                background: 'rgba(255,255,255,0.8)',
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '4px 8px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              ⋮
            </button>
            {menuOpen && (
              <div className="menu-dropdown" style={{ right: 0 }}>
                <button onClick={handleGenerateReleaseNotes}>📝 Generate Release Notes</button>
                <button onClick={() => handleSortTickets('issue_key', 'asc')}>🔼 Sort Issue Key ↑</button>
                <button onClick={() => handleSortTickets('issue_key', 'desc')}>🔽 Sort Issue Key ↓</button>
                <button onClick={() => handleSortTickets('status', 'asc')}>🔼 Sort Status A → Z</button>
                <button onClick={() => handleSortTickets('status', 'desc')}>🔽 Sort Status Z → A</button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="ticket-list" style={{ 
        flex: 1, 
        overflow: 'auto', 
        padding: '1rem',
        paddingTop: hideHeader ? '2.5rem' : '1rem' 
      }}>
        {sortedTickets.length === 0 && isDragActive && (
          <div className="drop-placeholder board-placeholder">Drop tickets here</div>
        )}
        
        {sortedTickets.length === 0 && !isDragActive && (
          <div className="board-placeholder">
            No tickets in this board
          </div>
        )}
        
        {sortedTickets.map((ticket) => (
          <div
            key={`${ticket.issue_key}-${ticket.sprint}`}
            onDragOver={handleDragOver}
            onDragEnter={(e) => handleDragEnterCard(e, ticket)}
            onDragLeave={handleDragLeaveCard}
            onDrop={(e) => handleDropCard(e, ticket)}
            className="ticket-wrapper"
          >
            {isDragActive && dragOverId === ticket.issue_key && (
              <div className="drop-placeholder" />
            )}
            <TicketCard
              ticket={ticket}
              draggable
              onDragStart={(e) => handleDragStartCard(e, ticket)}
              isNewDrop={ticket.issue_key === lastDroppedKey}
            />
          </div>
        ))}
        {isDragActive && dragOverId === null && sortedTickets.length > 0 && (
          <div className="drop-placeholder" />
        )}
      </div>
    </div>
  );
}

export default SprintBoard;